import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { okrService, keyResultService, departamentoService, okrAtividadeService, empresaService } from '@/services/api';
import { OKR, KeyResult, Departamento, OKRAtividade, Empresa } from '@/types';
import { AppLayout } from '@/components/layout/AppLayout';
import { GuiaModuloFlutuante } from '@/components/shared/GuiaModuloFlutuante';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Download, Loader2, Target, History, TrendingUp, ArrowUpDown, ArrowUp, ArrowDown, Filter, FileSpreadsheet, FileText, ChevronDown, ChevronLeft, ChevronRight, Building2, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import { Label } from '@/components/ui/label';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveOrShareBlob } from '@/lib/nativeFile';
import { getDeadlineStatus } from '@/lib/progress-utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const statusLabels: Record<string, string> = {
  a_fazer: 'A Fazer',
  fazendo: 'Fazendo',
  feito: 'Feito',
  pausado: 'Pausado',
  cancelado: 'Cancelado',
};

const atividadeTipoLabels: Record<string, string> = {
  criacao: 'Criação',
  atualizacao_status: 'Atualização de Status',
  atualizacao_key_result: 'Atualização de Key Result',
  cancelamento: 'Cancelamento',
  retorno_admin: 'Retorno do Admin',
  edicao: 'Edição',
};

const HISTORICO_PAGE_SIZE = 10;

export default function Relatorios() {
  const { user, empresaAtiva, empresaFiltroId } = useAuth();
  const [okrs, setOkrs] = useState<OKR[]>([]);
  const [keyResults, setKeyResults] = useState<KeyResult[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [atividades, setAtividades] = useState<OKRAtividade[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isHistoricoLoading, setIsHistoricoLoading] = useState(false);
  const [isHistoricoExporting, setIsHistoricoExporting] = useState(false);

  // Filtros para OKRs
  const [okrDepartamentoFilter, setOkrDepartamentoFilter] = useState<string>('all');
  const [okrPrazoFilter, setOkrPrazoFilter] = useState<string>('all');
  const [okrDataInicioFilter, setOkrDataInicioFilter] = useState<string>('');
  const [okrDataFimFilter, setOkrDataFimFilter] = useState<string>('');

  // Filtros para histórico
  const [historicoOkrFilter, setHistoricoOkrFilter] = useState<string>('all');
  const [historicoDepartamentoFilter, setHistoricoDepartamentoFilter] = useState<string>('all');
  const [historicoSortField, setHistoricoSortField] = useState<'data' | 'okr' | 'tipo' | 'usuario'>('data');
  const [historicoSortOrder, setHistoricoSortOrder] = useState<'asc' | 'desc'>('desc');
  const [historicoPage, setHistoricoPage] = useState(1);
  const [historicoTotal, setHistoricoTotal] = useState(0);
  const [historicoTotalPages, setHistoricoTotalPages] = useState(0);

  // Filtros para Key Results
  const [krOkrFilter, setKrOkrFilter] = useState<string>('all');
  const [krDepartamentoFilter, setKrDepartamentoFilter] = useState<string>('all');
  const [krSortField, setKrSortField] = useState<'okr' | 'descricao' | 'progresso' | 'percentual'>('okr');
  const [krSortOrder, setKrSortOrder] = useState<'asc' | 'desc'>('asc');

  const isAdmin = user?.tipo === 'admin' || user?.tipo === 'superadmin';
  const isSuperAdmin = user?.tipo === 'superadmin';
  // Empresa filtrada vem do seletor global do header: pro superadmin é o
  // `empresaFiltroId` (pode ser "todas as empresas"); pra admin/comum é a empresa vinculada.
  const empresaFilter = (isSuperAdmin ? empresaFiltroId : empresaAtiva?.id) ?? 'all';

  const loadData = async () => {
    if (!empresaAtiva?.id && user?.tipo !== 'superadmin') return;

    try {
      const empresaId = empresaAtiva?.id || '';

      const [okrsRes, krsRes, depsRes, empresasRes] = await Promise.all([
        user.tipo === 'superadmin'
          ? okrService.getAll()
          : okrService.getByEmpresa(empresaId),
        keyResultService.getAll(),
        user.tipo === 'superadmin'
          ? departamentoService.getAll()
          : departamentoService.getByEmpresa(empresaId),
        empresaService.getAll(),
      ]);

      if (okrsRes.success) setOkrs(okrsRes.data);
      if (krsRes.success) setKeyResults(krsRes.data);
      if (depsRes.success) setDepartamentos(depsRes.data);
      if (empresasRes.success) setEmpresas(empresasRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [user, empresaAtiva]);

  const loadHistorico = async () => {
    if (!empresaAtiva?.id && user?.tipo !== 'superadmin') return;

    try {
      setIsHistoricoLoading(true);
      const res = await okrAtividadeService.getPaginated({
        page: historicoPage,
        limit: HISTORICO_PAGE_SIZE,
        empresa_id: isSuperAdmin && empresaFilter !== 'all' ? empresaFilter : undefined,
        departamento_id: historicoDepartamentoFilter !== 'all' ? historicoDepartamentoFilter : undefined,
        okr_id: historicoOkrFilter !== 'all' ? historicoOkrFilter : undefined,
        sortField: historicoSortField,
        sortOrder: historicoSortOrder,
      });

      if (res.success) {
        setAtividades(res.data);
        setHistoricoTotal(res.pagination.total);
        setHistoricoTotalPages(res.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error loading historico:', error);
      toast.error('Erro ao carregar histórico de atividades');
    } finally {
      setIsHistoricoLoading(false);
    }
  };

  // Busca todas as páginas do histórico filtrado (a listagem em tela só mantém a página atual
  // carregada, mas exportar precisa do conjunto completo que bate com os filtros ativos).
  const fetchAllHistorico = async (): Promise<OKRAtividade[]> => {
    const EXPORT_PAGE_SIZE = 100;
    const baseParams = {
      empresa_id: isSuperAdmin && empresaFilter !== 'all' ? empresaFilter : undefined,
      departamento_id: historicoDepartamentoFilter !== 'all' ? historicoDepartamentoFilter : undefined,
      okr_id: historicoOkrFilter !== 'all' ? historicoOkrFilter : undefined,
      sortField: historicoSortField,
      sortOrder: historicoSortOrder,
    };

    const first = await okrAtividadeService.getPaginated({ page: 1, limit: EXPORT_PAGE_SIZE, ...baseParams });
    if (!first.success) return [];

    const all = [...first.data];
    const totalPages = first.pagination.totalPages;
    for (let page = 2; page <= totalPages; page++) {
      const res = await okrAtividadeService.getPaginated({ page, limit: EXPORT_PAGE_SIZE, ...baseParams });
      if (res.success) all.push(...res.data);
    }
    return all;
  };

  useEffect(() => {
    if (isAdmin) {
      loadHistorico();
    }
  }, [
    isAdmin,
    empresaAtiva,
    empresaFilter,
    historicoOkrFilter,
    historicoDepartamentoFilter,
    historicoSortField,
    historicoSortOrder,
    historicoPage,
  ]);

  // Reset filters when empresa changes
  useEffect(() => {
    setOkrDepartamentoFilter('all');
    setHistoricoDepartamentoFilter('all');
    setHistoricoOkrFilter('all');
    setHistoricoPage(1);
    setKrDepartamentoFilter('all');
    setKrOkrFilter('all');
  }, [empresaFilter]);

  const getDepartamento = (depId: string) => departamentos.find(d => d.id === depId);
  const getOKR = (okrId: string) => okrs.find(o => o.id === okrId);
  const getEmpresa = (empresaId: string) => empresas.find(e => e.id === empresaId);

  // Filter departamentos by selected empresa
  const filteredDepartamentos = useMemo(() => {
    if (!isSuperAdmin || empresaFilter === 'all') return departamentos;
    return departamentos.filter(d => d.empresa_id === empresaFilter);
  }, [departamentos, empresaFilter, isSuperAdmin]);

  // Filter OKRs list for selects (by empresa)
  const okrsForSelect = useMemo(() => {
    if (!isSuperAdmin || empresaFilter === 'all') return okrs;
    return okrs.filter(o => o.empresa_id === empresaFilter);
  }, [okrs, empresaFilter, isSuperAdmin]);

  // Filtered OKRs
  const filteredOkrs = useMemo(() => {
    let filtered = [...okrs];
    
    // Filter by empresa (superadmin)
    if (isSuperAdmin && empresaFilter !== 'all') {
      filtered = filtered.filter(o => o.empresa_id === empresaFilter);
    }
    
    // Filter by departamento
    if (okrDepartamentoFilter !== 'all') {
      filtered = filtered.filter(o => o.departamento_id === okrDepartamentoFilter);
    }
    
    // Filter by prazo status
    if (okrPrazoFilter !== 'all') {
      filtered = filtered.filter(o => {
        const status = getDeadlineStatus(o.data_fim);
        return status === okrPrazoFilter;
      });
    }
    
    // Filter by data inicio
    if (okrDataInicioFilter) {
      filtered = filtered.filter(o => o.data_inicio >= okrDataInicioFilter);
    }
    
    // Filter by data fim
    if (okrDataFimFilter) {
      filtered = filtered.filter(o => o.data_fim <= okrDataFimFilter);
    }
    
    return filtered;
  }, [okrs, empresaFilter, okrDepartamentoFilter, okrPrazoFilter, okrDataInicioFilter, okrDataFimFilter, isSuperAdmin]);

  // Histórico de atividades já vem filtrado, ordenado e paginado do backend.
  const filteredAtividades = useMemo(() => {
    return atividades;
  }, [atividades]);

  // Filtered and sorted Key Results
  const filteredKeyResults = useMemo(() => {
    let filtered = [...keyResults];
    
    // Filter by empresa (superadmin)
    if (isSuperAdmin && empresaFilter !== 'all') {
      const okrIdsInEmpresa = okrs.filter(o => o.empresa_id === empresaFilter).map(o => o.id);
      filtered = filtered.filter(kr => okrIdsInEmpresa.includes(kr.okr_id));
    }
    
    // Filter by OKR
    if (krOkrFilter !== 'all') {
      filtered = filtered.filter(kr => kr.okr_id === krOkrFilter);
    }
    
    // Filter by departamento
    if (krDepartamentoFilter !== 'all') {
      const okrIdsInDep = okrs.filter(o => o.departamento_id === krDepartamentoFilter).map(o => o.id);
      filtered = filtered.filter(kr => okrIdsInDep.includes(kr.okr_id));
    }
    
    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (krSortField) {
        case 'okr':
          const okrA = getOKR(a.okr_id)?.titulo || '';
          const okrB = getOKR(b.okr_id)?.titulo || '';
          comparison = okrA.localeCompare(okrB);
          break;
        case 'descricao':
          comparison = a.descricao.localeCompare(b.descricao);
          break;
        case 'progresso':
          comparison = a.progresso_atual - b.progresso_atual;
          break;
        case 'percentual':
          comparison = a.percentual - b.percentual;
          break;
      }
      return krSortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [keyResults, empresaFilter, krOkrFilter, krDepartamentoFilter, krSortField, krSortOrder, okrs, isSuperAdmin]);

  // Chart colors
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
  const STATUS_COLORS: Record<string, string> = {
    a_fazer: '#f59e0b',
    fazendo: '#3b82f6',
    pausado: '#64748b',
    feito: '#10b981',
    cancelado: '#ef4444',
  };

  // Chart data for Super Admin comparisons
  const chartDataByCompany = useMemo(() => {
    if (!isSuperAdmin) return { okrsByCompany: [], progressByCompany: [], statusByCompany: [], krByCompany: [] };

    // OKRs count by company
    const okrsByCompany = empresas.map(emp => ({
      name: emp.nome.length > 15 ? emp.nome.substring(0, 15) + '...' : emp.nome,
      fullName: emp.nome,
      okrs: okrs.filter(o => o.empresa_id === emp.id).length,
    })).filter(d => d.okrs > 0);

    // Average progress by company
    const progressByCompany = empresas.map(emp => {
      const companyOkrs = okrs.filter(o => o.empresa_id === emp.id);
      const avgProgress = companyOkrs.length > 0 
        ? Math.round(companyOkrs.reduce((acc, o) => acc + o.progresso_geral, 0) / companyOkrs.length)
        : 0;
      return {
        name: emp.nome.length > 15 ? emp.nome.substring(0, 15) + '...' : emp.nome,
        fullName: emp.nome,
        progresso: avgProgress,
      };
    }).filter(d => d.progresso > 0 || empresas.find(e => e.nome === d.fullName && okrs.some(o => o.empresa_id === e.id)));

    // Status distribution by company
    const statusByCompany = empresas.map(emp => {
      const companyOkrs = okrs.filter(o => o.empresa_id === emp.id);
      return {
        name: emp.nome.length > 15 ? emp.nome.substring(0, 15) + '...' : emp.nome,
        fullName: emp.nome,
        'A Fazer': companyOkrs.filter(o => o.status === 'a_fazer').length,
        'Fazendo': companyOkrs.filter(o => o.status === 'fazendo').length,
        'Pausado': companyOkrs.filter(o => o.status === 'pausado').length,
        'Feito': companyOkrs.filter(o => o.status === 'feito').length,
        'Cancelado': companyOkrs.filter(o => o.status === 'cancelado').length,
      };
    }).filter(d => d['A Fazer'] + d['Fazendo'] + d['Pausado'] + d['Feito'] + d['Cancelado'] > 0);

    // Key Results by company
    const krByCompany = empresas.map(emp => {
      const companyOkrIds = okrs.filter(o => o.empresa_id === emp.id).map(o => o.id);
      const companyKrs = keyResults.filter(kr => companyOkrIds.includes(kr.okr_id));
      const completed = companyKrs.filter(kr => kr.percentual === 100).length;
      const inProgress = companyKrs.filter(kr => kr.percentual > 0 && kr.percentual < 100).length;
      const notStarted = companyKrs.filter(kr => kr.percentual === 0).length;
      return {
        name: emp.nome.length > 15 ? emp.nome.substring(0, 15) + '...' : emp.nome,
        fullName: emp.nome,
        'Concluídos': completed,
        'Em Progresso': inProgress,
        'Não Iniciados': notStarted,
        total: companyKrs.length,
      };
    }).filter(d => d.total > 0);

    return { okrsByCompany, progressByCompany, statusByCompany, krByCompany };
  }, [empresas, okrs, keyResults, isSuperAdmin]);

  // Pie chart data for overall status distribution
  const overallStatusData = useMemo(() => {
    return [
      { name: 'A Fazer', value: okrs.filter(o => o.status === 'a_fazer').length, color: STATUS_COLORS.a_fazer },
      { name: 'Fazendo', value: okrs.filter(o => o.status === 'fazendo').length, color: STATUS_COLORS.fazendo },
      { name: 'Pausado', value: okrs.filter(o => o.status === 'pausado').length, color: STATUS_COLORS.pausado },
      { name: 'Feito', value: okrs.filter(o => o.status === 'feito').length, color: STATUS_COLORS.feito },
      { name: 'Cancelado', value: okrs.filter(o => o.status === 'cancelado').length, color: STATUS_COLORS.cancelado },
    ].filter(d => d.value > 0);
  }, [okrs]);

  const toggleSort = (field: 'data' | 'okr' | 'tipo' | 'usuario') => {
    setHistoricoPage(1);
    if (historicoSortField === field) {
      setHistoricoSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setHistoricoSortField(field);
      setHistoricoSortOrder('desc');
    }
  };

  const SortIcon = ({ field }: { field: 'data' | 'okr' | 'tipo' | 'usuario' }) => {
    if (historicoSortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return historicoSortOrder === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1" /> 
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const toggleKrSort = (field: 'okr' | 'descricao' | 'progresso' | 'percentual') => {
    if (krSortField === field) {
      setKrSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setKrSortField(field);
      setKrSortOrder('asc');
    }
  };

  const KrSortIcon = ({ field }: { field: 'okr' | 'descricao' | 'progresso' | 'percentual' }) => {
    if (krSortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return krSortOrder === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1" /> 
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  // Only admin can access this page - check after all hooks
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Export functions
  const exportToXLSX = async (data: Record<string, any>[], filename: string) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Dados');
    const arrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    await saveOrShareBlob(blob, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(`Arquivo ${filename}.xlsx exportado com sucesso`);
  };

  const exportToPDF = async (data: Record<string, any>[], filename: string, headers: string[], title: string) => {
    const doc = new jsPDF();
    
    doc.setFontSize(16);
    doc.text(title, 14, 15);
    doc.setFontSize(10);
    doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 22);
    
    const tableData = data.map(row => headers.map(h => String(row[h] ?? '')));
    
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 28,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [59, 130, 246] },
    });
    
    const blob = doc.output('blob');
    await saveOrShareBlob(blob, `${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success(`Arquivo ${filename}.pdf exportado com sucesso`);
  };

  const getOkrExportData = () => {
    return filteredOkrs.map(okr => ({
      Titulo: okr.titulo,
      Descricao: okr.descricao,
      Departamento: getDepartamento(okr.departamento_id)?.nome || '',
      Status: statusLabels[okr.status] || okr.status,
      Progresso: `${okr.progresso_geral}%`,
      DataInicio: okr.data_inicio ? new Date(okr.data_inicio).toLocaleDateString('pt-BR') : '',
      DataFim: okr.data_fim ? new Date(okr.data_fim).toLocaleDateString('pt-BR') : '',
    }));
  };

  const exportOKRsXLSX = () => exportToXLSX(getOkrExportData(), 'okrs');
  const exportOKRsPDF = () => exportToPDF(
    getOkrExportData(), 
    'okrs', 
    ['Titulo', 'Departamento', 'Status', 'Progresso', 'DataInicio', 'DataFim'],
    'Relatório de OKRs'
  );

  const getHistoricoExportData = (atividadesParaExportar: OKRAtividade[]) => {
    return atividadesParaExportar.map(ativ => ({
      OKR: getOKR(ativ.okr_id)?.titulo || '',
      Tipo: atividadeTipoLabels[ativ.tipo] || ativ.tipo,
      Descricao: ativ.descricao,
      Detalhes: ativ.detalhes || '',
      Usuario: ativ.usuario_nome,
      Data: new Date(ativ.data).toLocaleString('pt-BR'),
    }));
  };

  const exportHistorico = async (formato: 'xlsx' | 'pdf') => {
    setIsHistoricoExporting(true);
    try {
      const todasAtividades = await fetchAllHistorico();
      const data = getHistoricoExportData(todasAtividades);
      if (formato === 'xlsx') {
        await exportToXLSX(data, 'historico_okrs');
      } else {
        await exportToPDF(data, 'historico_okrs', ['OKR', 'Tipo', 'Descricao', 'Usuario', 'Data'], 'Histórico de Atividades OKRs');
      }
    } catch (error) {
      console.error('Error exporting historico:', error);
      toast.error('Erro ao exportar histórico de atividades');
    } finally {
      setIsHistoricoExporting(false);
    }
  };

  const exportHistoricoXLSX = () => exportHistorico('xlsx');
  const exportHistoricoPDF = () => exportHistorico('pdf');

  const getKrExportData = () => {
    return filteredKeyResults.map(kr => ({
      OKR: getOKR(kr.okr_id)?.titulo || '',
      Descricao: kr.descricao,
      ProgressoAtual: kr.progresso_atual,
      Meta: kr.progresso_meta,
      Percentual: `${kr.percentual}%`,
      Prazo: kr.prazo ? new Date(kr.prazo).toLocaleDateString('pt-BR') : '',
      Observacao: kr.aprovacao?.observacao || '',
    }));
  };

  const exportKRsXLSX = () => exportToXLSX(getKrExportData(), 'key_results');
  const exportKRsPDF = () => exportToPDF(
    getKrExportData(),
    'key_results',
    ['OKR', 'Descricao', 'ProgressoAtual', 'Meta', 'Percentual', 'Prazo', 'Observacao'],
    'Relatório de Key Results'
  );

  const clearOkrFilters = () => {
    setOkrDepartamentoFilter('all');
    setOkrPrazoFilter('all');
    setOkrDataInicioFilter('');
    setOkrDataFimFilter('');
  };

  const clearHistoricoFilters = () => {
    setHistoricoOkrFilter('all');
    setHistoricoDepartamentoFilter('all');
    setHistoricoPage(1);
  };

  const clearKrFilters = () => {
    setKrOkrFilter('all');
    setKrDepartamentoFilter('all');
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>

        <GuiaModuloFlutuante imagemSrc="/guias-modulos/relatorios.png" titulo="Relatórios" />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="rounded-lg border bg-card p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Relatórios</h1>
            <p className="text-muted-foreground">
              Visualize e exporte dados do sistema
            </p>
          </div>
        </div>

        <Tabs defaultValue={isSuperAdmin ? "graficos" : "okrs"} className="space-y-4">
          <TabsList className="flex-wrap h-auto">
            {isSuperAdmin && (
              <TabsTrigger value="graficos" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Comparativo
              </TabsTrigger>
            )}
            <TabsTrigger value="okrs" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              OKRs
            </TabsTrigger>
            <TabsTrigger value="historico" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Histórico de OKRs
            </TabsTrigger>
            <TabsTrigger value="keyresults" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Key Results
            </TabsTrigger>
          </TabsList>

          {/* Gráficos Comparativos Tab - Super Admin Only */}
          {isSuperAdmin && (
            <TabsContent value="graficos">
              <div className="grid gap-6">
                {/* Row 1: OKRs por Empresa e Distribuição de Status */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Building2 className="h-5 w-5" />
                        OKRs por Empresa
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartDataByCompany.okrsByCompany} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis type="number" className="text-xs" />
                            <YAxis dataKey="name" type="category" width={100} className="text-xs" />
                            <Tooltip 
                              contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                              formatter={(value, name, props) => [value, 'OKRs']}
                              labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                            />
                            <Bar dataKey="okrs" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Target className="h-5 w-5" />
                        Distribuição Geral de Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={overallStatusData}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              paddingAngle={2}
                              dataKey="value"
                              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                              labelLine={false}
                            >
                              {overallStatusData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip 
                              contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                            />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Row 2: Progresso Médio por Empresa */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <TrendingUp className="h-5 w-5" />
                      Progresso Médio por Empresa
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartDataByCompany.progressByCompany}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="name" className="text-xs" />
                          <YAxis domain={[0, 100]} className="text-xs" />
                          <Tooltip 
                            contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                            formatter={(value) => [`${value}%`, 'Progresso Médio']}
                            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                          />
                          <Bar dataKey="progresso" fill="#10b981" radius={[4, 4, 0, 0]}>
                            {chartDataByCompany.progressByCompany.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.progresso >= 80 ? '#10b981' : entry.progresso >= 50 ? '#f59e0b' : '#ef4444'} 
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Row 3: Status por Empresa */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <BarChart3 className="h-5 w-5" />
                      Status de OKRs por Empresa
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartDataByCompany.statusByCompany}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="name" className="text-xs" />
                          <YAxis className="text-xs" />
                          <Tooltip 
                            contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                          />
                          <Legend />
                          <Bar dataKey="A Fazer" stackId="a" fill="#f59e0b" />
                          <Bar dataKey="Fazendo" stackId="a" fill="#3b82f6" />
                          <Bar dataKey="Feito" stackId="a" fill="#10b981" />
                          <Bar dataKey="Cancelado" stackId="a" fill="#ef4444" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Row 4: Key Results por Empresa */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Target className="h-5 w-5" />
                      Key Results por Empresa
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartDataByCompany.krByCompany}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="name" className="text-xs" />
                          <YAxis className="text-xs" />
                          <Tooltip 
                            contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                          />
                          <Legend />
                          <Bar dataKey="Concluídos" fill="#10b981" />
                          <Bar dataKey="Em Progresso" fill="#3b82f6" />
                          <Bar dataKey="Não Iniciados" fill="#94a3b8" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* OKRs Tab */}
          <TabsContent value="okrs">
            <Card>
              <CardHeader className="space-y-4 pb-4">
                <div className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    OKRs ({filteredOkrs.length})
                  </CardTitle>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="gap-2">
                        <Download className="h-4 w-4" />
                        Exportar
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={exportOKRsXLSX} className="gap-2">
                        <FileSpreadsheet className="h-4 w-4" />
                        Excel (XLSX)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={exportOKRsPDF} className="gap-2">
                        <FileText className="h-4 w-4" />
                        PDF
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                {/* Filtros */}
                <div className="flex flex-wrap items-end gap-4 pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Filtros:</span>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Departamento</Label>
                    <Select value={okrDepartamentoFilter} onValueChange={setOkrDepartamentoFilter}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {filteredDepartamentos.map(dep => (
                          <SelectItem key={dep.id} value={dep.id}>
                            {dep.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Status Prazo</Label>
                    <Select value={okrPrazoFilter} onValueChange={setOkrPrazoFilter}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="overdue">Vencido</SelectItem>
                        <SelectItem value="warning">Próximo</SelectItem>
                        <SelectItem value="normal">No prazo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Data Início (de)</Label>
                    <Input
                      type="date"
                      value={okrDataInicioFilter}
                      onChange={(e) => setOkrDataInicioFilter(e.target.value)}
                      className="w-[150px]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Data Fim (até)</Label>
                    <Input
                      type="date"
                      value={okrDataFimFilter}
                      onChange={(e) => setOkrDataFimFilter(e.target.value)}
                      className="w-[150px]"
                    />
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearOkrFilters}>
                    Limpar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="w-full">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[200px]">Título</TableHead>
                        <TableHead className="min-w-[150px]">Departamento</TableHead>
                        <TableHead className="min-w-[100px]">Status</TableHead>
                        <TableHead className="min-w-[100px]">Progresso</TableHead>
                        <TableHead className="min-w-[120px]">Data Início</TableHead>
                        <TableHead className="min-w-[120px]">Data Fim</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredOkrs.map((okr) => {
                        const deadlineStatus = getDeadlineStatus(okr.data_fim);
                        return (
                          <TableRow key={okr.id}>
                            <TableCell className="font-medium">{okr.titulo}</TableCell>
                            <TableCell>{getDepartamento(okr.departamento_id)?.nome || '-'}</TableCell>
                            <TableCell>
                              <Badge variant="secondary">
                                {statusLabels[okr.status] || okr.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{okr.progresso_geral}%</TableCell>
                            <TableCell>{okr.data_inicio ? new Date(okr.data_inicio).toLocaleDateString('pt-BR') : '-'}</TableCell>
                            <TableCell>
                              <span className={
                                deadlineStatus === 'overdue' ? 'text-red-600 font-medium' :
                                deadlineStatus === 'warning' ? 'text-amber-600 font-medium' : ''
                              }>
                                {okr.data_fim ? new Date(okr.data_fim).toLocaleDateString('pt-BR') : '-'}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {filteredOkrs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            Nenhuma OKR encontrada
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Histórico Tab */}
          <TabsContent value="historico">
            <Card>
              <CardHeader className="space-y-4 pb-4">
                <div className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Histórico de Atividades ({historicoTotal})
                  </CardTitle>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="gap-2" disabled={isHistoricoExporting}>
                        {isHistoricoExporting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        Exportar
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={exportHistoricoXLSX} className="gap-2">
                        <FileSpreadsheet className="h-4 w-4" />
                        Excel (XLSX)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={exportHistoricoPDF} className="gap-2">
                        <FileText className="h-4 w-4" />
                        PDF
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                {/* Filtros */}
                <div className="flex flex-wrap items-end gap-4 pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Filtros:</span>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">OKR</Label>
                    <Select
                      value={historicoOkrFilter}
                      onValueChange={(value) => {
                        setHistoricoOkrFilter(value);
                        setHistoricoPage(1);
                      }}
                    >
                      <SelectTrigger className="w-[250px]">
                        <SelectValue placeholder="Todas as OKRs" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas as OKRs</SelectItem>
                        {okrsForSelect.map(okr => (
                          <SelectItem key={okr.id} value={okr.id}>
                            {okr.titulo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Departamento</Label>
                    <Select
                      value={historicoDepartamentoFilter}
                      onValueChange={(value) => {
                        setHistoricoDepartamentoFilter(value);
                        setHistoricoPage(1);
                      }}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {filteredDepartamentos.map(dep => (
                          <SelectItem key={dep.id} value={dep.id}>
                            {dep.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearHistoricoFilters}>
                    Limpar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-3 text-sm text-muted-foreground">
                  {filteredAtividades.length} de {historicoTotal} atividade(s)
                </div>
                <ScrollArea className="w-full">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead 
                          className="min-w-[200px] cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleSort('okr')}
                        >
                          <div className="flex items-center">
                            OKR
                            <SortIcon field="okr" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="min-w-[150px] cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleSort('tipo')}
                        >
                          <div className="flex items-center">
                            Tipo
                            <SortIcon field="tipo" />
                          </div>
                        </TableHead>
                        <TableHead className="min-w-[200px]">Descrição</TableHead>
                        <TableHead 
                          className="min-w-[150px] cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleSort('usuario')}
                        >
                          <div className="flex items-center">
                            Usuário
                            <SortIcon field="usuario" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="min-w-[150px] cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleSort('data')}
                        >
                          <div className="flex items-center">
                            Data
                            <SortIcon field="data" />
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isHistoricoLoading ? (
                        <TableRow>
                          <TableCell colSpan={5} className="py-8 text-center">
                            <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      ) : filteredAtividades.map((ativ) => (
                        <TableRow key={ativ.id}>
                          <TableCell className="font-medium">
                            {getOKR(ativ.okr_id)?.titulo || ativ.okr_id}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {atividadeTipoLabels[ativ.tipo] || ativ.tipo}
                            </Badge>
                          </TableCell>
                          <TableCell>{ativ.descricao}</TableCell>
                          <TableCell>{ativ.usuario_nome}</TableCell>
                          <TableCell>
                            {new Date(ativ.data).toLocaleString('pt-BR')}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!isHistoricoLoading && filteredAtividades.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                            Nenhuma atividade encontrada
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
                {historicoTotalPages > 1 && (
                  <div className="mt-3 flex items-center justify-between border-t pt-3">
                    <span className="text-sm text-muted-foreground">
                      Página {historicoPage} de {historicoTotalPages}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={historicoPage <= 1 || isHistoricoLoading}
                        onClick={() => setHistoricoPage((p) => Math.max(1, p - 1))}
                        title="Página anterior"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={historicoPage >= historicoTotalPages || isHistoricoLoading}
                        onClick={() => setHistoricoPage((p) => Math.min(historicoTotalPages, p + 1))}
                        title="Próxima página"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Key Results Tab */}
          <TabsContent value="keyresults">
            <Card>
              <CardHeader className="space-y-4 pb-4">
                <div className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Key Results ({filteredKeyResults.length})
                  </CardTitle>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="gap-2">
                        <Download className="h-4 w-4" />
                        Exportar
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={exportKRsXLSX} className="gap-2">
                        <FileSpreadsheet className="h-4 w-4" />
                        Excel (XLSX)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={exportKRsPDF} className="gap-2">
                        <FileText className="h-4 w-4" />
                        PDF
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                {/* Filtros */}
                <div className="flex flex-wrap items-end gap-4 pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Filtros:</span>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">OKR</Label>
                    <Select value={krOkrFilter} onValueChange={setKrOkrFilter}>
                      <SelectTrigger className="w-[250px]">
                        <SelectValue placeholder="Todas as OKRs" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas as OKRs</SelectItem>
                        {okrsForSelect.map(okr => (
                          <SelectItem key={okr.id} value={okr.id}>
                            {okr.titulo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Departamento</Label>
                    <Select value={krDepartamentoFilter} onValueChange={setKrDepartamentoFilter}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {filteredDepartamentos.map(dep => (
                          <SelectItem key={dep.id} value={dep.id}>
                            {dep.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearKrFilters}>
                    Limpar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="w-full">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead 
                          className="min-w-[200px] cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleKrSort('okr')}
                        >
                          <div className="flex items-center">
                            OKR
                            <KrSortIcon field="okr" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="min-w-[250px] cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleKrSort('descricao')}
                        >
                          <div className="flex items-center">
                            Descrição
                            <KrSortIcon field="descricao" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="min-w-[120px] cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleKrSort('progresso')}
                        >
                          <div className="flex items-center">
                            Progresso Atual
                            <KrSortIcon field="progresso" />
                          </div>
                        </TableHead>
                        <TableHead className="min-w-[100px]">Meta</TableHead>
                        <TableHead
                          className="min-w-[100px] cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleKrSort('percentual')}
                        >
                          <div className="flex items-center">
                            Percentual
                            <KrSortIcon field="percentual" />
                          </div>
                        </TableHead>
                        <TableHead className="min-w-[200px]">Observação</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredKeyResults.map((kr) => (
                        <TableRow key={kr.id}>
                          <TableCell className="font-medium">
                            {getOKR(kr.okr_id)?.titulo || kr.okr_id}
                          </TableCell>
                          <TableCell>{kr.descricao}</TableCell>
                          <TableCell>{kr.progresso_atual}</TableCell>
                          <TableCell>{kr.progresso_meta}</TableCell>
                          <TableCell>{kr.percentual}%</TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {kr.aprovacao?.observacao || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredKeyResults.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            Nenhum Key Result encontrado
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <GuiaModuloFlutuante imagemSrc="/guias-modulos/relatorios.png" titulo="Relatórios" />
    </AppLayout>
  );
}
